import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import GradientText from "../../shared/GradientText/GradientText";
import { Loader } from "semantic-ui-react";
import { useAlert } from "react-alert";
import { getSelfEnrolmentData } from "../../../selectors";

interface TimerProps {
  initialTime: number;
  resendTriggerHandler: () => void;
  loading: boolean;
}

const VerificationCountdown: React.FC<TimerProps> = ({
  initialTime,
  resendTriggerHandler,
  loading,
}) => {
  const [time, setTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [isResendAllowed, setIsResendAllowed] = useState(false);
  const [retries, setRetries] = useState(1);
  const [apiRetries, setApiRetries] = useState(0);
  const [isTimerReset, setIsTimerReset] = useState(false);
  const alert = useAlert();
  const [showErrorMsg, setShowErrorMsg] = useState(false);

  const [verificationCodeSentFailed] = useSelector((store: any) => [
    getSelfEnrolmentData(store)?.verificationCodeSentFailed,
  ]);

  //TODO: Make this generic
  useEffect(() => {
    if (verificationCodeSentFailed === true) {
      setShowErrorMsg(true);
      setIsRunning(false);
      setIsResendAllowed(true);
      setRetries(retries - 1);

      alert.show(
        { rawMessage: "Could not send verification code. Please try again." },
        {
          type: "error",
          timeout: 3000,
        }
      );
    }
  }, [verificationCodeSentFailed]);

  const dispatch = useDispatch();

  useEffect(() => {
    let intervalId: any;

    if (isRunning && time > 0) {
      intervalId = setInterval(() => {
        setTime((prevTime) => prevTime - 1);
        if (time - 1 === 0) {
          setIsRunning(false);
          setIsResendAllowed(true);
          if (retries === 3) {
            setIsTimerReset(true);
          }
          setRetries(1);
        }

        //Demo
        // if (time - 1 < 10 && retries <= 2) {
        //   setIsResendAllowed(true);
        // }

        if (time - 1 < 180 && retries <= 2) {
          setIsResendAllowed(true);
        }
      }, 1000);
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [isRunning, time]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    const formattedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
    const formattedSeconds =
      remainingSeconds < 10 ? `0${remainingSeconds}` : `${remainingSeconds}`;
    return `${formattedMinutes}:${formattedSeconds}`;
  };

  const triggerSendVerificationCode = () => {
    resendTriggerHandler();
  };

  //Send code
  const startTimer = () => {
    resendTriggerHandler();
    setIsRunning(true);
    setIsResendAllowed(false);
    setTime(initialTime);
    setApiRetries(apiRetries + 1);
  };

  //Resend code
  const resetTimer = () => {
    if (!isResendAllowed) return;

    resendTriggerHandler();

    setTime(initialTime);
    setIsResendAllowed(false);
    setRetries(retries + 1);
  };

  return (
    <div style={{ textAlign: "center" }}>
      {!isRunning && (
        <>
          <StyledContainer>
            {!loading && (
              <TimerContainer onClick={startTimer}>
                <GradientText
                  string={"Resend Verification Code"}
                  fontSize={"1rem"}
                  cursor="pointer"
                />
              </TimerContainer>
            )}
            {loading && (
              <TimerContainer>
                <Loader active inline="centered" size={"small"} />
              </TimerContainer>
            )}
          </StyledContainer>
        </>
      )}

      {isRunning && retries === 1 && !isTimerReset && (
        <>
          <GradientText
            string="Please check your email for the verification token."
            fontSize={"15.5px"}
            extraStyles={{
              fontStyle: "Italic",
            }}
          />
        </>
      )}

      {retries === 2 && !isTimerReset && (
        <>
          <GradientText
            string="Kindly await the arrival of the verification token in your email."
            fontSize={"15.5px"}
            extraStyles={{
              fontStyle: "Italic",
            }}
          />
        </>
      )}

      {retries === 3 && !isTimerReset && (
        <>
          <GradientText
            string="Please allow the ongoing process to complete before making another request for the verification token."
            fontSize={"15.5px"}
            extraStyles={{
              fontStyle: "Italic",
            }}
          />
          <GradientText
            string="Your cooperation is greatly appreciated."
            fontSize={"15.5px"}
            extraStyles={{
              marginTop: "1rem",
              fontStyle: "Italic",
            }}
          />
        </>
      )}

      {(retries > 3 || isTimerReset) && (
        <>
          <GradientText
            string={`Did not receive the verification token yet?`}
            fontSize={"15.5px"}
            extraStyles={{
              fontStyle: "Italic",
            }}
          />

          <GradientText
            string={`Kindly review your email for the verification token.`}
            fontSize={"15.5px"}
            extraStyles={{
              marginTop: "1rem",
              fontStyle: "Italic",
            }}
          />

          <GradientText
            string={`If you have not received it, please reach out to us at`}
            fontSize={"15.5px"}
            extraStyles={{
              fontStyle: "Italic",
              marginTop: "1rem",
            }}
          />
          <StyledLinkContainer
            target="_blank"
            href="mailto:support@ayacare.com"
          >
            <GradientText
              string={` support@ayacare.com`}
              fontSize={"14px"}
              inlineBlock
              cursor="pointer"
            />
          </StyledLinkContainer>
        </>
      )}

      {apiRetries >= 3 && !isTimerReset && !isRunning && (
        <>
          <GradientText
            string="Oops! Something is wrong. Please try again later."
            fontSize={"15.5px"}
            extraStyles={{
              fontStyle: "Italic",
            }}
          />
          <GradientText
            string={`If the problem persists, please contact us at`}
            fontSize={"15.5px"}
            extraStyles={{
              fontStyle: "Italic",
              marginTop: "1rem",
            }}
          />
          <StyledLinkContainer
            target="_blank"
            href="mailto:support@ayacare.com"
          >
            <GradientText
              string={` support@ayacare.com`}
              fontSize={"14px"}
              inlineBlock
              cursor="pointer"
            />
          </StyledLinkContainer>
        </>
      )}

      {isRunning && (
        <>
          <StyledContainer>
            {!loading && (
              <TimerContainer onClick={resetTimer}>
                <GradientText
                  string={"Resend Verification Token"}
                  fontSize={"1rem"}
                  cursor="pointer"
                  isDisabled={!isResendAllowed}
                />
                <div
                  style={{
                    fontSize: "0.8rem",
                  }}
                >
                  Time remaining: {formatTime(time)}
                </div>
              </TimerContainer>
            )}
            {loading && (
              <TimerContainer>
                <Loader active inline="centered" size={"small"} />
              </TimerContainer>
            )}
          </StyledContainer>
        </>
      )}
    </div>
  );
};

const TimerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 1.75rem;
`;

const StyledContainer = styled.div`
  border: 1px solid #e5e7eb;
  padding: 0.5rem 0.5rem;
  border-radius: 16px;
  box-shadow: 0px 2px 2px rgba(170, 171, 193, 0.2);
  margin-bottom: 2rem;

  display: flex;
  justify-content: space-evenly;
  align-items: center;
  margin-top: 1.5rem;
`;

const StyledLinkContainer = styled.a`
  margin-right: 0.5rem;

  &:first-child {
    margin-left: 0.5rem;
  }
`;

export default VerificationCountdown;
